import { type FC, useEffect, useRef, useState } from "react";

import clsx from "clsx";

const SMALL_DOCS = [
  { left: 0, top: 8 },
  { left: 30, top: 2 },
  { left: 60, top: 10 },
];

const LARGE_DOCS = [
  { left: 0, top: 0 },
  { left: 30, top: 4 },
  { left: 60, top: 0 },
  { left: 90, top: 5 },
  { left: 120, top: 1 },
  { left: 14, top: 34 },
  { left: 44, top: 31 },
  { left: 74, top: 35 },
  { left: 104, top: 32 },
  { left: 0, top: 66 },
  { left: 30, top: 63 },
  { left: 60, top: 67 },
  { left: 90, top: 64 },
  { left: 120, top: 66 },
];

const EXCERPTS = ["p. 412", "p. 1,847", "p. 2,306"];

// Markup transcribed from the design port kit (sections/Scale.html). Every part
// of this graphic is HTML+CSS driven by home.css keyframes; the animation plays
// once and rests on its final frame, gated by .sa2-play.
export const ScaleArt: FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (!("IntersectionObserver" in window)) {
      setPlaying(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        setPlaying(true);
        observer.disconnect();
      },
      { threshold: 0.35 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={clsx("scaleart", playing && "sa2-play")}
      aria-label="Small records and large records pass through the same engine; it widens with the load; cited excerpts come out either way"
    >
      <span className="sa2-halo"></span>
      <span className="sa2-gate"></span>

      <div className="sa2-grps">
        {SMALL_DOCS.map((doc) => (
          <span
            key={`${doc.left}-${doc.top}`}
            className="sa2-doc"
            style={{ left: doc.left, top: doc.top }}
          ></span>
        ))}
      </div>
      <span className="sa2-lb sa2-lb1">Small records</span>

      <div className="sa2-grp">
        {LARGE_DOCS.map((doc) => (
          <span
            key={`${doc.left}-${doc.top}`}
            className="sa2-doc"
            style={{ left: doc.left, top: doc.top }}
          ></span>
        ))}
      </div>
      <span className="sa2-lb sa2-lb2">Large records</span>

      {["sa2-x1", "sa2-x2"].map((variant) => (
        <div key={variant} className={clsx("sa2-out", variant)}>
          <div className="sa2-ck">Excerpts ready</div>
          {EXCERPTS.map((page) => (
            <div key={page} className="sa2-ex">
              <i></i>
              <b>{page}</b>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
